/**
 * ClerkProvider multi-domain (satellite) props for the .co -> .com migration.
 *
 * One Vercel deploy serves both hosts, so `isSatellite` must be decided per-request from
 * the URL host rather than baked in at build time — hence the function forms. `signInUrl`
 * always points at the PRIMARY domain: credential entry happens there, then Clerk's
 * handshake syncs the session back onto the satellite.
 *
 * Returns {} (single-domain, no-op) when `satelliteHost` is falsy, so dev, the brands
 * deploy, and the post-migration single-domain state need no code change — just leave
 * NEXT_PUBLIC_CLERK_SATELLITE_HOST unset.
 */
export interface ClerkMultiDomainProps {
  isSatellite?: (url: URL) => boolean;
  domain?: (url: URL) => string;
  signInUrl?: string;
}

export function getClerkMultiDomainProps(
  satelliteHost?: string,
  primarySignInUrl?: string
): ClerkMultiDomainProps {
  if (!satelliteHost) {
    return {};
  }

  return {
    isSatellite: (url: URL) => url.host === satelliteHost,
    domain: (url: URL) => url.host,
    signInUrl: primarySignInUrl,
  };
}

/**
 * Where to send a signed-out visitor who landed on the satellite sign-in page.
 *
 * Credential entry is not allowed on a satellite domain — Clerk rejects it ("This operation
 * is not allowed on a satellite domain"). The satellite must redirect to the PRIMARY domain's
 * sign-in, carrying the current satellite URL as `redirect_url` so Clerk returns there and
 * syncs the session back via its handshake after login.
 *
 * Returns null (i.e. render <SignIn> locally, the normal single-domain case) when satellite
 * is not configured or the visitor is already on the primary domain.
 */
export function getSatelliteSignInRedirect(
  current: { host: string; href: string },
  satelliteHost?: string,
  primarySignInUrl?: string
): string | null {
  if (!satelliteHost || !primarySignInUrl) {
    return null;
  }
  if (current.host !== satelliteHost) {
    return null;
  }

  const url = new URL(primarySignInUrl);
  url.searchParams.set('redirect_url', current.href);
  return url.toString();
}
